// @ts-nocheck
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

import PreTitleComponent, {
    PreTitleStyles,
    PreTitleProps as PreTitleComponentProps,
} from "@store/Library/Components/PreTitle/PreTitle";

const enum fields {
    text = "text",
    dotSize = "dotSize",
    variant = "variant",
    forwardAs = "forwardAs",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.dotSize]: number;
        [fields.variant]: string;
        [fields.forwardAs]: PreTitleComponentProps["forwardAs"];
        [fields.customCSS]: string;
    };
}

type PreTitleVariant = "" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "bannerTitle" | "bannerSecondaryTitle";

const defaultDotSizeByVariant: Partial<Record<Exclude<PreTitleVariant, "">, number>> = {
    p: 8,
    h6: 8,
    h5: 9,
    h4: 10,
    h3: 12,
    h2: 14,
    h1: 16,
    bannerTitle: 16,
    bannerSecondaryTitle: 14,
};

const getAutoDotSize = (variant: PreTitleVariant) => {
    if (!variant) {
        return 10;
    }
    return defaultDotSizeByVariant[variant] ?? 10;
};

const PreTitleWidget: FC<Props> = ({ fields: { text, dotSize, variant, forwardAs, customCSS } }) => {
    const styles: PreTitleStyles = useMemo(() => {
        if (!customCSS) {
            return {};
        }

        return {
            text: {
                css: css`
                    ${customCSS}
                `,
            },
        };
    }, [customCSS]);

    const resolvedVariant = (variant ?? "") as PreTitleVariant;

    const resolvedDotSize = Number.isFinite(dotSize) && dotSize > 0 ? dotSize : getAutoDotSize(resolvedVariant);

    const componentVariant: PreTitleComponentProps["variant"] | undefined =
        resolvedVariant === "bannerTitle" || resolvedVariant === "bannerSecondaryTitle"
            ? undefined
            : (resolvedVariant as PreTitleComponentProps["variant"]) || undefined;

    return (
        <PreTitleComponent
            text={text}
            dotSize={resolvedDotSize}
            variant={componentVariant}
            forwardAs={forwardAs || undefined}
            extendedStyles={styles}
        />
    );
};

const defaultCustomCss = `& {
}`;

const advancedTab = { displayName: "Advanced", sortOrder: 1 };
const basicTab = { displayName: "Basic", sortOrder: 0 };

const widgetModule: WidgetModule = {
    component: PreTitleWidget,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Text",
                fieldType: "Contextual", // Store Custom Code
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.dotSize,
                displayName: "Dot Size (px)",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                name: fields.variant,
                displayName: "Format",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Match Default", value: "" },
                    { displayName: "Paragraph", value: "p" },
                    { displayName: "Heading 1", value: "h1" },
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                    { displayName: "Banner", value: "bannerTitle" },
                    { displayName: "Banner Secondary", value: "bannerSecondaryTitle" },
                ],
                defaultValue: "",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.forwardAs,
                displayName: "HTML Tag",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Match Format", value: "" },
                    { displayName: "span", value: "span" },
                    { displayName: "p", value: "p" },
                    { displayName: "h1", value: "h1" },
                    { displayName: "h2", value: "h2" },
                    { displayName: "h3", value: "h3" },
                    { displayName: "h4", value: "h4" },
                    { displayName: "h5", value: "h5" },
                    { displayName: "h6", value: "h6" },
                ],
                defaultValue: "",
                hideEmptyOption: true,
                tooltip: "Set a HTML tag that differs from the visual format for SEO purposes",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
